<template>
  <div>
    <!-- Confirmation Request Timeout -->
    <div class="form-group">
      <label class="col-sm-3 col-md-3 control-label">{{ $t("CONFIRMATION_REQUEST_TIMEOUT") }}
        <a v-tooltip.right="$t('CONFIRMATION_REQUEST_TIMEOUT_TT')" tooltip-placement="right"
          tooltip-class="tooltip-white" tooltip-append-to-body="true">
          &nbsp;&nbsp;<i class="fa fa-question-circle"></i>
        </a>
      </label>
      <div class="col-sm-1 col-md-1">
        <input
          type="number"
          class="form-control input-sm"
          min="0"
          v-model="connectionTimeoutValue"
          @change="connectionTimeoutChange"
          @wheel="$event.target.blur()"
          />
      </div>
    </div>
    <!-- Confirmation Request Required -->
    <div class="form-group">
      <label class="col-xs-12 col-sm-3 col-md-3 control-label"> {{ $t("CONFIRMATION_REQUEST_REQUIRED")}}
        <a v-tooltip.right="$t('CONFIRMATION_REQUEST_REQUIRED_TT')" tooltip-placement="right"
          tooltip-class="tooltip-white" tooltip-append-to-body="true">
          &nbsp;&nbsp;<i class="fa fa-question-circle"></i>
        </a>
      </label>
      <div class="col-sm-2 col-md-1 col-xs-3 toggle-wrap">
        <nzToggle v-model="confirmationRequestRequiredValue" @onToggle="confirmationRequestRequiredChange">
        </nzToggle>
      </div>
    </div>
    <!-- Use Number From Confirmation Request -->
    <div class="form-group">
      <label class="col-xs-12 col-sm-3 col-md-3 control-label"> {{$t("CONFIRMATION_REQUEST_USE_NUMBER")}}
        <a v-tooltip.right="$t('CONFIRMATION_REQUEST_USE_NUMBER_TT')" tooltip-placement="right"
          tooltip-class="tooltip-white" tooltip-append-to-body="true">
          &nbsp;&nbsp;<i class="fa fa-question-circle"></i>
        </a>
      </label>
      <div class="col-sm-2 col-md-1 col-xs-3 toggle-wrap">
        <nzToggle v-model="useConfirmationRequestNumberValue" @onToggle="useConfirmationRequestNumberChange">
        </nzToggle>
      </div>
    </div>
    <HttpRequest
      :request-status="confirmationSettingsRequestStatus.status"
      :request-settings="currentRequestSettings"
      :enable-multi-line-url="true"
      :enable-macros="enableMacros"
      @runHttpRequest="runRequest"
      @updateRequestSettings="updateConfirmationRequestSettings"
    />
    <HttpRequestResponse
      :request-response-body="requestResponseBody"
      :request-error="requestError"
      :request-response-code="requestResponseCode"
      :request-status="confirmationSettingsRequestStatus.status"
      :completed-request="completedRequest"
    />
    <ConfirmationRequestParsingSettings
      v-if="showParsingSettings"
      :enable-dynamic-dial="useConfirmationRequestNumber"
      :dynamic-number-parsing.sync="dynamicNumberParsing"
      :selectable-properties-config="selectablePropertiesConfig"
      @updateDynamicNumberParsingSettings="updateDynamicNumberParsingSettings"
    />
  </div>
</template>

<script>

import nzToggle from '@/core/components/nzToggle/nzToggleSlim.vue';
import HttpRequest from '@/core/components/HttpForm/HttpRequest';
import HttpRequestResponse from '@/core/components/HttpForm/HttpRequest/HttpRequestResponse';
import ConfirmationRequestParsingSettings from './ConfirmationRequestParsingSettings.vue';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { extractProperties } from '@/modules/dynamicInboundCalls/helpers/customScoring';
import { STATUS_SUCCESS, STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';
import { mapGetters, mapActions } from 'vuex'
import { sanitizeRequestPropertyForRingTreeTargetForm } from '../helpers/requestSettings';
import { defaultParsingStep } from '../helpers/constants';

export default {
  name: 'ConfirmationRequestSettings',
  components: {nzToggle, HttpRequest, HttpRequestResponse, ConfirmationRequestParsingSettings},
  props: {
    dynamicNumberParsing: {
      type: Array,
      default: () => {
        return [{ ...defaultParsingStep }];
      }
    },
    connectionTimeout: {
      type: Number,
      default: null
    },
    confirmationRequestRequired: {
      type: Boolean,
      default: false
    },
    useConfirmationRequestNumber: {
      type: Boolean,
      default: false
    },
    currentRequestSettings: {
      type: Object,
      default: () => ({
        url: '',
        method: 'get',
        body: '',
        authentication: '',
        headers: {},
        contentType: 'application/json'
      })
    },
    enableMacros: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      useConfirmationRequestNumberValue: this.useConfirmationRequestNumber,
      confirmationRequestRequiredValue: this.confirmationRequestRequired,
      connectionTimeoutValue: this.connectionTimeout,
      requestResponseBody: null,
      requestError: null,
      requestResponseCode: null,
      completedRequest: null,
      selectablePropertiesConfig: {
        options: [],
        placeholder: 'Choose Property',
      },
    };
  },
  computed: {
    ...mapGetters('ringTreeTargets', ['confirmationSettingsRequestStatus']),

    confirmationSettingsRequestLoading() {
      return this.confirmationSettingsRequestStatus.status == STATUS_PENDING;
    },
    confirmationSettingsRequestSuccess() {
      return this.confirmationSettingsRequestStatus.status == STATUS_SUCCESS;
    },
    confirmationSettingsRequestFail() {
      return this.confirmationSettingsRequestStatus.status == STATUS_ERROR;
    },
    showParsingSettings() {
      return (this.dynamicNumberParsing?.length || this.confirmationSettingsRequestSuccess || this.showAllParsingSettings) && !this.confirmationSettingsRequestLoading;
    },
  },
  destroyed() {
    this.resetConfirmationSettingsRequest();
  },
  methods: {
    ...mapActions('ringTreeTargets', ['confirmationSettingsRequest', 'resetConfirmationSettingsRequest']),
    async runRequest(payload) {
      this.resetResponseData();
      const requestPayload = {
        ...payload.requestSettings,
        url: payload.completedRequestUrl,
        body: JSON.stringify(payload.completedRequestBody)
      }
      try {
        const response = await this.confirmationSettingsRequest(requestPayload);
        this.requestResponseCode = response.responseCode;
        this.completedRequest = {
          url: requestPayload.url,
          body: requestPayload.body,
          method: requestPayload.method
        }
        if (this.confirmationSettingsRequestSuccess) { 
          this.requestResponseBody = JSON.parse(response.body);
          if (!this.requestResponseBody) {
            this.requestError = this.$t('PING_TREE_TARGET_REQUEST_SETTINGS_EMPTY_RESPONSE');
            return;
          }
          const propertiesArray = extractProperties(this.requestResponseBody);
          this.selectablePropertiesConfig.options = propertiesArray;
        } else if (this.confirmationSettingsRequestFail) {
          this.requestError = this.confirmationSettingsRequestStatus.error;
          this.requestResponseBody = JSON.parse(response.body);
        }
      } catch (e) {
        this.requestError = this.confirmationSettingsRequestStatus.error;
      }
    },
    resetResponseData() {
      this.requestResponseBody = null;
      this.requestError = null;
      this.requestResponseCode = null;
      this.completedRequest = null;
    },
    updateDynamicNumberParsingSettings() {
      const parsingFormattedForRttForm = sanitizeRequestPropertyForRingTreeTargetForm(this.dynamicNumberParsing);

      this.$emit('dynamicNumberParsingSecondStepChange', parsingFormattedForRttForm);
    },
    useConfirmationRequestNumberChange() {
      this.useConfirmationRequestNumberValue = !this.useConfirmationRequestNumberValue
      this.$emit('useConfirmationRequestNumberChange', this.useConfirmationRequestNumberValue)
    },
    confirmationRequestRequiredChange() {
      this.confirmationRequestRequiredValue = !this.confirmationRequestRequiredValue
      this.$emit('confirmationRequestRequiredChange', this.confirmationRequestRequiredValue)
    },
    connectionTimeoutChange() {
      this.$emit('connectionTimeoutChange', this.connectionTimeoutValue)
    },
    updateConfirmationRequestSettings(payload) {
      this.$emit('updateConfirmationRequestSettings', payload);
    }

  },
    i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>